/**
 * Where the request pages plug into the pages around them (tracker item 45,
 * phase 4): "Request service" on the home page and the unlinked screen, and
 * "Ask to reschedule" on a job. Phase 3 left these as stubs; the real pieces
 * are PortalRequestPages.tsx (request service) and PortalReschedule.tsx.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PortalJob, PortalMe } from '../../api/types';
import { primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { PORTAL_REQUEST_SERVICE_PATH, PORTAL_REQUESTS_PATH } from '../../utils/portal';
import { usePortalApi } from './PortalContext';
import { PortalReschedule } from './PortalReschedule';

const linkButton = 'inline-flex items-center justify-center text-center min-h-[44px]';

/**
 * Request service and Your requests. Works for a linked customer and for an unlinked but
 * email-verified login (a prospect), so it is on the unlinked home too. The open request
 * count is a courtesy: if it cannot be loaded nothing is shown.
 */
export function RequestServiceEntry({ me }: { me: Pick<PortalMe, 'status'> }) {
  const api = usePortalApi();
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const result = await api.listPortalServiceRequests({ page: 1, limit: 50 });
        if (active) setOpen(result.data.filter((r) => r.status === 'NEW').length);
      } catch {
        if (active) setOpen(null);
      }
    })();
    return () => { active = false; };
  }, [api]);

  const linked = me.status === 'LINKED';
  return (
    <section
      aria-labelledby="request-service-heading"
      className={linked ? 'rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200' : 'mt-4 border-t border-gray-100 pt-4'}
      data-testid="request-service-entry"
    >
      <h2 id="request-service-heading" className="text-sm font-semibold text-gray-600">Need something done?</h2>
      <p className="mt-1 text-sm text-gray-700">
        {linked
          ? 'Ask for a new service at one of your properties. We will reply here and by email.'
          : 'You can ask for a service now, even before we have matched your account. We will reply here and by email.'}
      </p>
      {open !== null && open > 0 && (
        <p className="mt-1 text-xs text-gray-600" data-testid="open-requests">
          You have {open} open request{open === 1 ? '' : 's'}.
        </p>
      )}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Link to={PORTAL_REQUEST_SERVICE_PATH} className={`${primaryButtonClass} ${linkButton} text-base`}>Request service</Link>
        <Link to={PORTAL_REQUESTS_PATH} className={`${secondaryButtonClass} ${linkButton}`}>Your requests</Link>
      </div>
    </section>
  );
}

/** Ask to reschedule on a job detail page; `onChanged` reloads the job after the visit moved. */
export function JobRescheduleSlot({ job, onChanged }: { job: PortalJob; onChanged?: () => void }) {
  return <PortalReschedule job={job} onChanged={onChanged} />;
}
